import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link as LinkIcon, ArrowUpRight, ArrowLeft, CheckCircle2,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { getServiceBySlug, SERVICES, servicePath, serviceSlug } from "../lib/constants";

const ICON_MAP = {
  FileText, RefreshCw, ShoppingCart, Smartphone, Apple,
  Headphones, Settings, Link: LinkIcon,
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = ICON_MAP[service.icon] || FileText;
  const slugs = SERVICES.map((s) => serviceSlug(s.title));
  const idx = slugs.indexOf(slug);
  const prevSlug = slugs[(idx - 1 + slugs.length) % slugs.length];
  const nextSlug = slugs[(idx + 1) % slugs.length];
  const prev = getServiceBySlug(prevSlug);
  const next = getServiceBySlug(nextSlug);

  return (
    <>
      <PageHero
        eyebrow="Services"
        breadcrumb={[{ label: "Services", path: "/services" }, { label: service.title }]}
        title={service.intro}
        lede={service.paragraphs[0]}
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="w-14 h-14 rounded-2xl bg-[#00C2FF]/15 border border-[#00C2FF]/40 flex items-center justify-center text-[#00C2FF] mb-6">
              <Icon className="w-6 h-6" />
            </div>
            {service.paragraphs.map((p, i) => (
              <p key={i} className="text-sm text-gray-300 leading-relaxed mb-4">{p}</p>
            ))}
          </div>
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] mb-4">
              / Deliverables
            </div>
            <ul className="space-y-3">
              {service.highlights.map((h, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#00C2FF] shrink-0 mt-1" />
                  <span className="text-sm text-gray-200">{h}</span>
                </motion.li>
              ))}
            </ul>
            <Link to="/contact" className="btn-cyan inline-flex items-center gap-2 mt-10 px-7 py-3.5 rounded-full font-semibold">
              Get started <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 gap-4">
          {prev && (
            <Link to={servicePath(prev.slug)} className="group p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 flex items-center gap-4">
              <ArrowLeft className="w-5 h-5 text-[#00C2FF]" />
              <div>
                <div className="font-mono text-[10px] uppercase text-gray-500">Previous</div>
                <div className="font-semibold mt-1">{prev.title}</div>
              </div>
            </Link>
          )}
          {next && (
            <Link to={servicePath(next.slug)} className="group p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 sm:justify-end sm:text-right flex items-center gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase text-gray-500">Next</div>
                <div className="font-semibold mt-1">{next.title}</div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-[#00C2FF]" />
            </Link>
          )}
        </div>
      </section>
    </>
  );
}
